import { PRODUCT_NAME } from "@mailhand/core/brand";
import { tenantIdSchema } from "@mailhand/core/schemas";
import { type ReactNode, useEffect, useState } from "react";
import { Icon, Select } from "../ui/index.js";
import { Link, type RouteName, navigate } from "./router.js";
import { useApi, useSession, useSessions } from "./services.js";
import { SignOutButton } from "./signOut.js";
import { isSetupComplete, useTenant } from "./tenant.js";
import { useTask } from "./useTask.js";
import { billingBanner } from "../screens/settings/billingText.js";

interface NavItem {
  to: string;
  label: string;
  routes: readonly RouteName[];
  count?: number;
}

function navItems(setupDone: boolean, pendingApprovals: number): NavItem[] {
  const items: NavItem[] = [
    { to: "/activity", label: "Activity", routes: ["activity", "message"] },
    { to: "/approvals", label: "Approvals", routes: ["approvals"], count: pendingApprovals },
    { to: "/actions", label: "Actions", routes: ["actions", "actionNew", "actionEdit"] },
    { to: "/settings", label: "Settings", routes: ["settings"] },
  ];
  return setupDone ? items : [{ to: "/setup", label: "Setup", routes: ["setup"] }, ...items];
}

export function BrandMark() {
  return (
    <span className="brand">
      <svg className="brand-mark" viewBox="3 1 23 27" width="20" height="23" aria-hidden="true">
        <circle cx="20" cy="5.5" r="2.5" fill="none" stroke="currentColor" strokeWidth="2.4" />
        <path d="M20 8v11a6.5 6.5 0 0 1-13 0v-4l4 3.5" fill="none" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      <span className="brand-name">{PRODUCT_NAME}</span>
    </span>
  );
}

function TenantSwitcher() {
  const session = useSession();
  const sessions = useSessions();
  const api = useApi();
  const task = useTask();
  if (session === null || session.tenants.length < 2 || session.active === null) return null;

  async function switchTo(value: string): Promise<void> {
    const tenantId = tenantIdSchema.parse(value);
    const ok = await task.run(async () => { sessions.set(await api.call("authSwitch", { body: { tenantId } })); });
    if (ok) navigate("/activity");
  }

  return (
    <div className="switcher">
      <Select aria-label="Switch business" value={session.active} disabled={task.busy} onChange={(event) => void switchTo(event.target.value)}>
        {session.tenants.map((tenant) => (
          <option key={tenant.id} value={tenant.id}>
            {tenant.name}
          </option>
        ))}
      </Select>
    </div>
  );
}

/** On every screen: at a monthly hold, or from 80% of an allowance. */
function BillingBanner() {
  const { billing } = useTenant();
  const banner = billingBanner(billing);
  if (banner === null) return null;
  return (
    <Link to="/settings#billing" className="banner banner-warn">
      <span>{banner.text}</span>
      <span className="banner-cta">{banner.cta}</span>
    </Link>
  );
}

export function Shell({ route, children }: { route: RouteName; children: ReactNode }) {
  const { view, actions, pendingApprovals } = useTenant();
  const [menuOpen, setMenuOpen] = useState(false);
  const items = navItems(isSetupComplete(view, actions), pendingApprovals);

  useEffect(() => { setMenuOpen(false); }, [route]);

  return (
    <div className="app">
      <header className={`nav-wrap${menuOpen ? " is-open" : ""}`}>
        <nav className="nav" aria-label="Main">
          <Link to="/activity" className="nav-brand" aria-label={`${PRODUCT_NAME} home`}>
            <BrandMark />
          </Link>
          <div className="nav-links" id="nav-links">
            {items.map((item) => (
              <Link key={item.to} to={item.to} className="nav-link" aria-current={item.routes.includes(route) ? "page" : undefined}>
                {item.label}
                {item.count === undefined || item.count === 0 ? null : <span className="nav-count mono">{item.count}</span>}
              </Link>
            ))}
          </div>
          <div className="nav-end">
            {view.tenant.state === "paused" ? <span className="nav-paused mono">Paused</span> : null}
            <TenantSwitcher />
            <SignOutButton />
          </div>
          <button
            type="button"
            className="icon-button nav-toggle"
            aria-expanded={menuOpen}
            aria-controls="nav-links"
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            onClick={() => { setMenuOpen((open) => !open); }}
          >
            {pendingApprovals > 0 && !menuOpen ? <span className="nav-toggle-dot" aria-hidden="true" /> : null}
            <Icon name={menuOpen ? "x" : "menu"} size={18} />
          </button>
        </nav>
      </header>
      <main className="page">
        <BillingBanner />
        {children}
      </main>
    </div>
  );
}
